import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { Loader } from '../components';
import { Table } from '../components/table/table';
import { runInAction } from 'mobx';
import {
  StudioPageProps,
  useCoworkerFocusSubscription,
  useCrossTabNodeSync,
  useSolutionExistenceSubscription,
} from '../hooks';
import { WithLoadingState } from '../router';
import { tableStore } from '../stores/table-store';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { Stack } from '@pypestream/design-system';
import {
  TranslationComponent,
  TranslationKeys,
} from '@pypestream/translations';
import { usePresenceMobXBridge } from '../hooks/use-presence-mobx-bridge';
import { useBuilderEntry } from '../hooks/use-builder-entry';

type BuilderPageProps = StudioPageProps;

// Renders nothing — just runs the subscription outside the Table render tree.
// PSTUDIO-3953: `observer` because useCoworkerFocusSubscription reads
// `tableStore.solutionVersionId` and both re-subscribes AND asserts identity on
// it. Without it the read registers no dependency, so the value only refreshes
// when something else happens to re-render this component — and since the
// identity is now also what admits a coworker push, a lagging read makes the
// guard silently drop EVERY push instead of merely applying it late.
//
// To be precise about what changes: BuilderPage is itself an observer and reads
// the same field, so this child was already unmounted deterministically on a
// switch via the loader branch — a real, if indirect, dependency. What the
// wrapper buys is that the child no longer depends on the PARENT happening to
// re-render for a reason that has nothing to do with it. It also makes
// useSolutionExistenceSubscription reactive, which reads the same field here.
const CoworkerFocusSubscription = observer(() => {
  useCoworkerFocusSubscription();
  usePresenceMobXBridge();
  useSolutionExistenceSubscription(); // PSTUDIO-3671: cross-machine delete detect
  return null;
});

// PSTUDIO-4168: cross-tab node sync (PSTUDIO-3610) is mounted OUTSIDE the
// `isLoading` branch, unlike the subscriptions above.
//
// Its effect holds the whole protocol in closure state — `isDirty`, the 3s
// throttle's `lastReloadAt`, the in-flight flag and the 15s watchdog — and has
// an empty dep array, so unmounting it does not pause the protocol, it ERASES
// it. Sitting inside the branch, every publish erased it twice: a publish flips
// the table to `loading` on the way out (the version bump) and back on the way
// in. A sibling tab's broadcast landing in that window reached no subscriber at
// all, so the tab stayed on its pre-publish snapshot with nothing left to
// retry — and node numbers are allocated from exactly that local snapshot
// (`getExistingNodeNumbers`), which is how two tabs come to hand out the same
// one.
//
// Running it while the table loads is safe and is the point: `shouldReloadNodes`
// already refuses on `currentState === 'loading'`, and `reloadRetryDelayMs`
// classifies that as a block which clears with no event we listen for, so the
// hook keeps `isDirty` and re-checks on its own backoff until the table is
// ready. The signal is deferred instead of lost.
const CrossTabNodeSync = () => {
  useCrossTabNodeSync();
  return null;
};

const BuilderPage: React.FC<BuilderPageProps> = observer(() => {
  const {
    botName,
    draftVersion,
    customerId,
    botId,
    projectId,
    projectName,
    selectedBotName,
    solutionVersionId,
  } = useProStudioCtxSelector((ctx) => ({
    botName: ctx.botName,
    customerId: ctx.customerId,
    projectId: ctx.project?.projectProStudioSetting?.id,
    botId:
      ctx.customerId && ctx.botName
        ? `${ctx.customerId}.${ctx.botName}`
        : undefined,
    deployedVersions: ctx.deployedVersions,
    draftVersion: ctx.draftVersion,
    projectName: ctx.project?.name,
    selectedBotName: ctx.selectedBot?.displayName,
    // See the note in use-table-store-sync: the key form is a property of the row.
    solutionVersionId: ctx.solutionVersionPsId ?? '',
  }));

  useEffect(() => {
    if (
      customerId &&
      botName &&
      draftVersion !== '' &&
      draftVersion !== undefined &&
      draftVersion !== null &&
      projectId !== null &&
      projectId !== '' &&
      botId
    ) {
      runInAction(() => {
        tableStore.botId = botId;
        tableStore.solutionVersionId = solutionVersionId;
        if (projectId) {
          tableStore.projectProStudioSettingId = projectId;
        }
        tableStore.draftVersion = draftVersion;
      });
    }
  }, [solutionVersionId, customerId, botName, draftVersion, botId, projectId]);

  // PSTUDIO-4240: the entrance the effect above cannot be. It can only ASSIGN,
  // and assigning an unchanged id is a no-op for the reaction that issues the
  // fetch — so a return into this page that never released the identity left a
  // store parked in the waiting state exactly as it found it.
  //
  // Declared AFTER the assignment, and that order is what makes a genuine
  // version switch refuse. Not by the id comparison — after the assignment the
  // two ids MATCH — but because the reaction runs synchronously on that
  // assignment and its forced cleanup lowers both waiting flags, so the rule's
  // second refusal is the one that fires. The hook's docblock names the lines;
  // `hooks/use-builder-entry` also owns the replay guard and is where the
  // wiring is tested.
  useBuilderEntry(tableStore, solutionVersionId);

  useEffect(() => {
    return () => {
      runInAction(() => {
        // Deliberately UNFORCED: a visit that is still legitimately waiting for
        // backend data must survive the departure. Forcing it here would drop
        // both waiting flags and unblock Publish for a version the backend has
        // not finished setting up — the direction PSTUDIO-4240 rejects. The
        // re-entry above is what re-establishes the data instead.
        tableStore.cleanupSolutionVersionSubscription(false);
      });
    };
  }, []); // Empty deps - only cleanup on unmount

  // Treat the table as loading until tableStore points at the current
  // solution. Without this, switching solutions briefly shows the previous
  // solution's rows (the page-level loader hides as soon as XState reaches
  // idle, which is before tableStore has fetched the new workflow data).
  const tableMatchesCurrentSolution =
    !!customerId &&
    !!botName &&
    !!draftVersion &&
    tableStore.solutionVersionId === solutionVersionId;
  const isLoading =
    tableStore.currentState === 'loading' || !tableMatchesCurrentSolution;

  return (
    <>
      {/* PSTUDIO-4168: outside the branch — survives the loading flip a publish
          causes, so a sibling tab's broadcast is deferred, never lost. */}
      <CrossTabNodeSync />
      {/* PSTUDIO-4240 (WCAG 2.1 AA, 4.1.3 Status Messages): this entrance
          introduces a state change nobody triggered — the table swaps to a
          loader and back as the re-entry's fetch resolves. The shared `Loader`
          announces by default (PSTUDIO-4241); this page passes
          `announce={false}` because the region below already covers both
          edges, and a second region would say "Loading workflows" twice.

          The region is mounted PERMANENTLY and only its text changes, which is
          the part that makes it work: a live region inserted into the DOM
          together with its content is announced inconsistently across screen
          readers, so the obvious `role="status"` on the overlay itself would
          have covered the start of loading at best, and never its end. Here
          both transitions are announced, and "loaded" is the one that matters
          — it is the moment the table is usable again. */}
      <div role="status" className="u-visually-hidden">
        <TranslationComponent
          i18nKey={
            (isLoading
              ? 'pro-studio/common:workflowsTableStatus.loading'
              : 'pro-studio/common:workflowsTableStatus.loaded') as TranslationKeys
          }
        />
      </div>
      {isLoading ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <Stack
            direction="column"
            gutter="medium"
            alignItems="center"
            style={{ textAlign: 'center' }}
          >
            {/* PSTUDIO-4241: silent on purpose. The region above is mounted
                permanently and already announces both edges of this state;
                letting the loader speak too would say "Loading workflows"
                twice and still never say it finished. */}
            <Loader relative announce={false} />
          </Stack>
        </div>
      ) : (
        <>
          <CoworkerFocusSubscription />
          <Table projectName={projectName} botName={selectedBotName} />
        </>
      )}
    </>
  );
});

export const Builder = WithLoadingState(BuilderPage);
